import { pdfjs } from "react-pdf";

const MIN_TEXT_LENGTH = 500;

/**
 * Extract full text from a PDF file using pdf.js.
 * Returns null if extracted text is shorter than MIN_TEXT_LENGTH chars.
 */
export async function extractFullText(file: File): Promise<string | null> {
  const arrayBuffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;

  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .filter((item) => "str" in item)
      .map((item) => (item as { str: string }).str)
      .join(" ");
    pages.push(pageText);
  }

  const fullText = pages.join("\n\n");
  return fullText.length >= MIN_TEXT_LENGTH ? fullText : null;
}

interface TextItemWithTransform {
  str: string;
  transform: number[];
}

/**
 * Extract a likely title from page 1 of a PDF by finding the largest-font
 * text block in the top third of the page.
 */
export async function extractTitleFromPdf(
  file: File,
): Promise<string | null> {
  const arrayBuffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  const page = await doc.getPage(1);
  const content = await page.getTextContent();
  const viewport = page.getViewport({ scale: 1 });
  const topThirdY = viewport.height * (2 / 3); // PDF y-axis goes bottom-up

  // Filter to items in the top third of the page with non-empty text
  const candidates = (content.items as Array<Record<string, unknown>>).filter(
    (item) =>
      typeof item.str === "string" &&
      Array.isArray(item.transform) &&
      item.str.trim().length > 0 &&
      item.transform.length >= 6 &&
      (item.transform as number[])[5] >= topThirdY,
  ) as unknown as TextItemWithTransform[];

  if (candidates.length === 0) return null;

  // Find the largest font size (transform[0] is the font scale)
  const maxFontSize = Math.max(...candidates.map((c) => Math.abs(c.transform[0])));

  // Collect all items with that font size (or within 10% tolerance)
  const threshold = maxFontSize * 0.9;
  const titleItems = candidates.filter(
    (c) => Math.abs(c.transform[0]) >= threshold,
  );

  const title = titleItems
    .map((item) => item.str.trim())
    .join(" ")
    .trim();

  return title.length > 3 ? title : null;
}

/**
 * Extract a DOI from page 1 text of a PDF.
 * Looks for patterns like "10.XXXX/..." which is the universal DOI format.
 */
export async function extractDoiFromPdf(
  file: File,
): Promise<string | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const page = await doc.getPage(1);
    const content = await page.getTextContent();

    const text = content.items
      .filter((item) => "str" in item)
      .map((item) => (item as { str: string }).str)
      .join(" ");

    return extractDoiFromText(text);
  } catch {
    return null;
  }
}

/**
 * Check if a string looks like a plausible paper title.
 * Rejects single common words, very short strings, and generic headers.
 */
function isPlausibleTitle(text: string): boolean {
  if (!text || text.length < 15) return false;
  const words = text.split(/\s+/).filter((w) => w.length > 1);
  if (words.length < 3) return false;
  return true;
}

/**
 * Extract both title and DOI from page 1 in a single pdf.js load.
 * Checks PDF document metadata first (more reliable), then falls back
 * to font-size heuristic on page 1 text.
 */
export async function extractTitleAndDoiFromPdf(
  file: File,
): Promise<{ title: string | null; doi: string | null }> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;

    // --- Try PDF document metadata first (most reliable) ---
    let metaTitle: string | null = null;
    let metaDoi: string | null = null;
    try {
      const meta = await doc.getMetadata();
      const info = (meta?.info ?? {}) as Record<string, unknown>;
      if (typeof info.Title === "string" && info.Title.trim().length > 3) {
        metaTitle = info.Title.trim();
      }
      // Some publishers store DOI in Subject or custom fields
      for (const field of ["Subject", "Keywords", "WPS-ARTICLEDOI"]) {
        if (!metaDoi && typeof info[field] === "string") {
          metaDoi = extractDoiFromText(info[field] as string);
        }
      }
    } catch {
      // metadata not available — continue with text extraction
    }

    const page = await doc.getPage(1);
    const content = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1 });

    // DOI from full page text
    const fullText = content.items
      .filter((item) => "str" in item)
      .map((item) => (item as { str: string }).str)
      .join(" ");
    const textDoi = extractDoiFromText(fullText);
    // Prefer page-text DOI (visible, reliable); metadata DOI as fallback only
    const doi = textDoi || metaDoi;

    // Title from largest font in top third
    const topThirdY = viewport.height * (2 / 3);
    const candidates = (content.items as Array<Record<string, unknown>>).filter(
      (item) =>
        typeof item.str === "string" &&
        Array.isArray(item.transform) &&
        (item.str as string).trim().length > 0 &&
        (item.transform as number[]).length >= 6 &&
        (item.transform as number[])[5] >= topThirdY,
    ) as unknown as TextItemWithTransform[];

    let fontTitle: string | null = null;
    if (candidates.length > 0) {
      const maxFontSize = Math.max(...candidates.map((c) => Math.abs(c.transform[0])));
      const threshold = maxFontSize * 0.9;
      const titleItems = candidates.filter(
        (c) => Math.abs(c.transform[0]) >= threshold,
      );
      const t = titleItems.map((item) => item.str.trim()).join(" ").trim();
      fontTitle = t.length > 3 ? t : null;
    }

    // Prefer metadata title if font-extracted title looks like garbage
    let title: string | null;
    if (isPlausibleTitle(fontTitle ?? "")) {
      title = fontTitle;
    } else if (isPlausibleTitle(metaTitle ?? "")) {
      title = metaTitle;
    } else {
      // Both are poor — use whichever is longer
      title = (metaTitle && metaTitle.length > (fontTitle?.length ?? 0))
        ? metaTitle
        : fontTitle;
    }

    return { title, doi };
  } catch {
    return { title: null, doi: null };
  }
}

/**
 * Extract a DOI from raw text using regex.
 * Handles: "doi: 10.xxx/yyy", "https://doi.org/10.xxx/yyy", inline "10.xxx/yyy".
 */
export function extractDoiFromText(text: string): string | null {
  const doiRegex = /\b(10\.\d{4,9}\/[^\s,;)\]}>]+)/i;
  const match = doiRegex.exec(text);
  if (match) {
    // Clean trailing punctuation that isn't part of the DOI
    return match[1].replace(/[.,;)\]>]+$/, "").toLowerCase();
  }
  return null;
}

/**
 * Extract a potential paper title from a PDF filename.
 * Handles Zotero connector patterns like "Author et al. - 2024 - Title.pdf".
 */
export function extractTitleFromFilename(filename: string): string | null {
  // Remove extension
  const name = filename.replace(/\.pdf$/i, "");

  // Pattern: "Author(s) - Year - Title"
  const withYear = name.match(/^.+?\s*-\s*\d{4}\s*-\s*(.+)$/);
  if (withYear && withYear[1].length > 10) {
    return withYear[1].trim();
  }

  // Pattern: "Author(s) - Title" (no year)
  const noYear = name.match(/^[^-]+\s*-\s*(.{10,})$/);
  if (noYear) {
    return noYear[1].trim();
  }

  // If the filename itself is reasonably descriptive (>20 chars), use it as-is.
  // This handles publisher filenames that might be partial titles.
  if (name.length > 20) {
    return name;
  }

  return null;
}
